"use client"

import { useMemo, useState, useEffect } from "react"
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query"
import { toast } from "sonner"
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useRouter } from '@tanstack/react-router'
import { useRepository } from "@city-sage/aurum-avis-labs-components"
import { QueryKeys } from "../../lib/QueryKeys"
import { SearchBoxFeatureSuggestion, SearchBoxRetrieveResponse } from "@mapbox/search-js-core"
import { Trash2, MapPin, Heart, Loader2 } from "lucide-react"
import CompactHeader from "../CompactHeader"
import { LocationSearchSection } from "../create-stop/LocationSearchSection"
import MiniDayMapView from "../create-stop/MiniDayMapView"
import { ImageGallerySection } from "../ImageGallerySection"
import { TimingSection } from "../create-stop/TimingSection"
import { DescriptionSection } from "../create-stop/DescriptionSection"
import { CreateStopFooter } from "../create-stop/CreateStopFooter"
import { DaySelectionSection } from "../create-stop/DaySelectionSection"
import { SavedStopsSheet } from "../create-stop/SavedStopsSheet"
import { StopFormValues, PreviewImage } from "../types/createStopTypes"
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle, 
  AlertDialogTrigger,
  Button
} from "@city-sage/aurum-avis-labs-components"
import { useDayStops } from "../../hooks/useDayStops"
import { ChatButton } from "../chat/ChatButton"
import { MarkupEditorSheet } from "../MarkupEditorSheet"
import { uploadFileCompressed } from "../../lib/imageUtils"
import { Image as ImageType } from "@city-sage/core-client"

// Get Mapbox access token from environment
const MAPBOX_ACCESS_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || ''

// Form schema for stop details - conditional validation based on timing choice
const createStopFormSchema = (requireTiming: boolean) => z.object({
  startTime: requireTiming ? z.string().optional() : z.string().optional(),
  durationMinutes: requireTiming 
    ? z.number().min(1, "Duration must be at least 1 minute").optional().or(z.literal(''))
    : z.any().optional(),
  description: z.string().optional(),
  highlight: z.string().optional()
})

interface StopFormPageProps {
  itineraryId: string
  dayId: string
  stopId?: string
}

export function StopFormPage({ itineraryId, dayId, stopId }: StopFormPageProps) {
  const { api } = useRepository()
  const queryClient = useQueryClient()
  const router = useRouter()


  // Query for existing stop data if editing
  const { data: stopData } = useQuery({
    queryKey: ['stop', itineraryId, stopId],
    queryFn: async () => {
      if (!stopId) return null
      const stop = await api.findOneItineraryStop({ itineraryId, id: stopId });
      return stop;
    },
    enabled: !!itineraryId && !!stopId,
  })

  // Determine if we're editing or creating
  const isEditing = !!stopId && !!stopData

  const defaultValues = useMemo(() => {
    if (!stopData) return {
      startTime: undefined,
      durationMinutes: undefined,
      description: undefined,
      highlight: undefined
    }
    
    return {
      startTime: stopData.startTime || undefined,
      durationMinutes: stopData.durationMinutes || undefined,
      description: stopData.description || undefined,
      highlight: stopData.highlight || undefined
    }
  }, [stopData])
  const [selectedLocation, setSelectedLocation] = useState<SearchBoxRetrieveResponse | null>(null)
  const selectedLocationFeature = useMemo<SearchBoxFeatureSuggestion | null>(() => {
    if (!selectedLocation) return null
    return selectedLocation.features[0]
  }, [selectedLocation])

  const [inputValue, setInputValue] = useState("")
  const [showTimingFields, setShowTimingFields] = useState(false)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [uploadedImage, setUploadedImage] = useState<ImageType | null>(null)
  const [uploadedImagePreview, setUploadedImagePreview] = useState<string | null>(null)
  const [isUsingUploadedImage, setIsUsingUploadedImage] = useState(false)
  const [selectedDayId, setSelectedDayId] = useState<string>(dayId)
  const [isSavedStopsSheetOpen, setIsSavedStopsSheetOpen] = useState(false)
  const [isMarkupEditorOpen, setIsMarkupEditorOpen] = useState(false)
  const [isHighlightMarkupEditorOpen, setIsHighlightMarkupEditorOpen] = useState(false)

  // Form setup with dynamic schema based on timing fields
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    clearErrors,
    reset
  } = useForm<StopFormValues>({
    resolver: zodResolver(createStopFormSchema(showTimingFields)),
    defaultValues
  })

  // Reset form when stopData loads
  useEffect(() => {
    if (stopData) {
      reset(defaultValues)
      setShowTimingFields(!!(stopData.startTime || stopData.durationMinutes))
    }
  }, [stopData, defaultValues, reset])

  // Clear form errors when switching between timing modes
  useEffect(() => {
    clearErrors()
  }, [showTimingFields, clearErrors])

  const watchedDuration = watch('durationMinutes')

  // Mutation to upload custom image
  const uploadImageMutation = useMutation({
    mutationFn: async (file: File) => {
      return uploadFileCompressed(file, api, {
        maxWidth: 1920,
        maxHeight: 1080,
        quality: 0.8
      })
    },
    onSuccess: (data) => {
      setUploadedImage(data.url)
      setIsUsingUploadedImage(true)
      toast.success("Image uploaded successfully!")
    },
    onError: () => {
      toast.error("Failed to upload image. Please try again.")
      setUploadedImagePreview(null)
    }
  })

  // Query for current day data to get existing stops and calculate next index
  const {data: dayStops} = useDayStops({
    itineraryId,
    dayId
  })

  // Query for all days in the itinerary for day selection
  const { data: allDays } = useQuery({
    queryKey: QueryKeys.itinerary.days(itineraryId),
    queryFn: async () => {
      return api.findAllItineraryDay({ 
        itineraryId, 
        sort: JSON.stringify(["number", "ASC"]),
        // TODO this is hardcoded for now, we need to fix this
        limit: 1000,
      });
    },
    enabled: !!itineraryId,
  });

  

  // Calculate the next index for the new stop
  const nextStopIndex = useMemo(() => {
    if (!dayStops || dayStops.length === 0) {
      return 0; // First stop starts at index 0
    }
    
    // Find the highest index among existing stops
    const maxIndex = Math.max(...dayStops.map(stop => stop.index || 0));
    return maxIndex + 1;
  }, [dayStops]);

  // Initialize state with existing stop data if editing
  useEffect(() => {
    if (isEditing && stopData && selectedLocationFeature === null) {
      // Set up location from existing stop
      setInputValue(stopData.title)
      // Create a mock SearchBoxRetrieveResponse from stop data
      const mockLocation: SearchBoxRetrieveResponse = {
        features: [{
          geometry: {
            type: 'Point',
            coordinates: [stopData.place.coordinates.longitude, stopData.place.coordinates.latitude]
          },
          properties: {
            name: stopData.title,
            context: {
              street: { name: stopData.place.address.addressLine1 },
              place: { name: stopData.place.address.city },
              region: { name: stopData.place.address.state || '' },
              postcode: { name: stopData.place.address.postalCode || '' },
              country: { 
                name: stopData.place.address.countryName, 
                country_code: stopData.place.address.countryCode,
              }
            }
          }
        }]
      } as SearchBoxRetrieveResponse
      setSelectedLocation(mockLocation)
      
      // Set up existing image if present
      if (stopData.image) {
        setUploadedImage(stopData.image)
        setIsUsingUploadedImage(true)
      }

      // Set the selected day to the stop's current day (first day in dayIds)
      if (stopData.dayIds && stopData.dayIds.length > 0) {
        setSelectedDayId(stopData.dayIds[0])
      }
    }
  }, [isEditing, stopData, selectedLocationFeature])

  // Mutation to create a new stop
  const createStopMutation = useMutation({
    mutationFn: async (data: { location: SearchBoxFeatureSuggestion; formData: StopFormValues }) => {
      console.log("Creating stop for location:", data.location)
      
      // Get selected image URL - either uploaded or from preview
      let selectedImage: ImageType | undefined
      if (isUsingUploadedImage && uploadedImage) {
        selectedImage = uploadedImage
      } else if (previewImages && previewImages[selectedImageIndex]) {
        selectedImage = previewImages[selectedImageIndex]
      }
      const stopData = await api.createStop({
        itineraryId,
        createStopDto: {
          index: nextStopIndex,
          title: data.location.properties.name,
          mapboxId: data.location.properties.mapbox_id as string,
          coordinates: {
            latitude: data.location.geometry.coordinates[1],
            longitude: data.location.geometry.coordinates[0]
          },
          dayIds: [selectedDayId],
          address: {
            addressLine1: data.location.properties.context?.street?.name ?? "",
            addressLine2: undefined,
            city: data.location.properties.context?.place?.name ?? "",
            state: data.location.properties.context?.region?.name ?? "",
            postalCode: data.location.properties.context?.postcode?.name ?? "",
            countryName: data.location.properties.context?.country?.name ?? "",
            countryCode: data.location.properties.context?.country?.country_code ?? ""
          },
          startTime: showTimingFields ? (data.formData.startTime || undefined) : undefined,
          durationMinutes: showTimingFields ? (data.formData.durationMinutes || undefined) : undefined,
          description: data.formData.description || undefined,
          highlight: data.formData.highlight || undefined,
          imageUrl: selectedImage?.url || undefined
        }
      })
      
      return stopData
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.itinerary.days(itineraryId) })
      queryClient.invalidateQueries({ queryKey: QueryKeys.itinerary.stops(itineraryId) })
      toast.success(`Stop "${data.title}" added successfully!`)
      router.history.back()
    },
    onError: (error) => {
      console.error("Failed to create stop:", error)
      toast.error("Failed to create stop. Please try again.")
    },
  })



  // Mutation to update existing stop
  const updateStopMutation = useMutation({
    mutationFn: async (formData: StopFormValues) => {
      console.log("Updating stop:", stopId)
      
      // Get selected image URL - either uploaded or from preview  
      let selectedImage: ImageType | undefined
      if (isUsingUploadedImage && uploadedImage) {
        selectedImage = uploadedImage
      } else if (previewImages && previewImages[selectedImageIndex]) {
        selectedImage = previewImages[selectedImageIndex]
      }

      const updateData = {
        title: selectedLocationFeature?.properties.name || stopData?.title,
        description: formData.description,
        highlight: formData.highlight,
        startTime: showTimingFields ? formData.startTime : undefined,
        durationMinutes: showTimingFields ? formData.durationMinutes : undefined,
        imageUrl: selectedImage?.url || undefined,
        dayIds: [selectedDayId]
      }

      const updatedStop = await api.updateStop({
        itineraryId,
        id: stopId!,
        updateStopRequest: updateData
      })
      
      return updatedStop
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.itinerary.days(itineraryId) })
      queryClient.invalidateQueries({ queryKey:  QueryKeys.itinerary.stops(itineraryId) })
      queryClient.invalidateQueries({ queryKey: QueryKeys.itinerary.day(itineraryId, dayId) })
      queryClient.invalidateQueries({ queryKey: QueryKeys.itinerary.day(itineraryId, selectedDayId) })
      toast.success(`Stop "${data.title}" updated successfully!`)
      router.history.back()
    },
    onError: (error) => {
      console.error("Failed to update stop:", error)
      toast.error("Failed to update stop. Please try again.")
    },
  })

  // Mutation to delete existing stop
  const deleteStopMutation = useMutation({
    mutationFn: async () => {
      console.log("Deleting stop:", stopId)
      await api.removeStop({
        itineraryId,
        id: stopId!
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.itinerary.days(itineraryId) })
      queryClient.invalidateQueries({ queryKey:  QueryKeys.itinerary.stops(itineraryId) })
      queryClient.invalidateQueries({ queryKey: QueryKeys.itinerary.day(itineraryId, dayId) })
      queryClient.invalidateQueries({ queryKey: QueryKeys.itinerary.day(itineraryId, selectedDayId) })
      toast.success("Stop deleted successfully!")
      router.history.back()
    },
    onError: (error) => {
      console.error("Failed to delete stop:", error)
      toast.error("Failed to delete stop. Please try again.")
    },
  })

  // Query for preview images when a location is selected
  const { data: previewImages, isLoading: imagesLoading } = useQuery({
    queryKey: ['stop-preview-images', selectedLocationFeature?.id, itineraryId, selectedLocationFeature?.properties.name],
    queryFn: async () => {
      if (!selectedLocationFeature) return []
      return api.searchAndSavePreviewImagesForStop({ 
        itineraryId,
        searchAndSavePreviewImagesForStopRequest: {
          locationQuery: selectedLocationFeature?.properties.name,
          count: 3 
        }
      })
    },
    enabled: !!selectedLocationFeature,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

  // Event handlers
  const handleGoBack = () => {
    router.history.back()
  }

  const handleLocationSelect = (result: SearchBoxRetrieveResponse) => {
    console.log("Location selected:", result)
    setSelectedLocation(result)
  }

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Show preview immediately
      const reader = new FileReader()
      reader.onloadend = () => {
        setUploadedImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
      
      // Upload the file
      uploadImageMutation.mutate(file)
    }
  }

  const handlePreviewImageSelect = (index: number) => {
    if (index === -1) {
      // Uploaded image selected
      setIsUsingUploadedImage(true)
    } else {
      // Preview image selected
      setSelectedImageIndex(index)
      setIsUsingUploadedImage(false)
    }
  }

  const handleRemoveUploadedImage = () => {
    setUploadedImage(null)
    setUploadedImagePreview(null)
    setIsUsingUploadedImage(false)
  }

  const onSubmit = handleSubmit((formData) => {
    if (isEditing) {
      updateStopMutation.mutate(formData)
    } else if (selectedLocationFeature) {
      createStopMutation.mutate({ 
        location: selectedLocationFeature, 
        formData 
      })
    }
  })

  const isSubmitting = isEditing ? updateStopMutation.isPending : createStopMutation.isPending

  return (
          <div className="min-h-dvh bg-gray-50 flex flex-col">
      {/* Add scrollbar hide styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
        `
      }} />

      {/* Header */}
      <CompactHeader 
        backTo={`/itineraries/${itineraryId}/days/${dayId}/edit`}
        icon={MapPin}
        title={isEditing ? "Edit Stop" : "Add Stop"}
        subtitle={isEditing ? "Modify stop details" : "Add location to your itinerary"}
      />

      {/* Content */}
      <div className="flex-1 overflow-auto mt-2">
        {/* Search Section */}
        <LocationSearchSection
          accessToken={MAPBOX_ACCESS_TOKEN}
          inputValue={inputValue}
          onInputChange={setInputValue}
          onLocationSelect={handleLocationSelect}
          selectedLocationFeature={selectedLocationFeature}
        />

        {/* Saved Stops Option - Only show when creating (not editing) */}
        {!isEditing && (
          <div className="px-4 py-3 bg-white border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Heart className="h-4 w-4 text-pink-500" />
                <span className="text-sm font-medium text-gray-700">Add from saved stops</span>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsSavedStopsSheetOpen(true)}
                className="text-xs"
              >
                Browse
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Add a location you've previously saved
            </p>
          </div>
        )}
           {/* Mini Map Preview */}
           <MiniDayMapView
              itineraryId={itineraryId}
              dayId={dayId}
              newStopLocation={selectedLocationFeature ? {
                latitude: selectedLocationFeature.geometry.coordinates[1],
                longitude: selectedLocationFeature.geometry.coordinates[0],
                title: selectedLocationFeature.properties.name
              }: undefined}
            />
        {selectedLocationFeature && (
          <>
            {/* Day Selection Section - Only show in edit mode if there are multiple days */}
            {isEditing && (
              <DaySelectionSection
                days={allDays}
                selectedDayId={selectedDayId}
                onDaySelect={setSelectedDayId}
                currentDayId={dayId}
              />
            )}

            {/* Image Gallery Section */}
            <ImageGallerySection
              previewImages={previewImages}
              imagesLoading={imagesLoading}
              selectedImageIndex={selectedImageIndex}
              uploadedImage={uploadedImage}
              uploadedImagePreview={uploadedImagePreview}
              isUsingUploadedImage={isUsingUploadedImage}
              isUploading={uploadImageMutation.isPending}
              onImageUpload={handleImageUpload}
              onPreviewImageSelect={handlePreviewImageSelect}
              onRemoveUploadedImage={handleRemoveUploadedImage}
            />

            {/* Timing Section */}
            <TimingSection
              showTimingFields={showTimingFields}
              onToggleTimingFields={setShowTimingFields}
              watchedDuration={watchedDuration}
              register={register}
              errors={errors}
              setValue={setValue}
            />

            {/* Description Section */}
            <DescriptionSection
              register={register}
              errors={errors}
              onOpenMarkupEditor={() => setIsMarkupEditorOpen(true)}
              onOpenHighlightMarkupEditor={() => setIsHighlightMarkupEditorOpen(true)}
            />
          </>
        )}
      </div>

<div className="p-4">
      <form onSubmit={onSubmit}>
        <div className="flex gap-3">
          <Button 
            type="button"
            variant="outline" 
            onClick={handleGoBack}
            className="flex-1 h-11"
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            disabled={selectedLocation === null || isSubmitting}
            className="flex-1 h-11"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isEditing ? "Updating..." : "Adding..."}
              </>
            ) : (
              <>
                <MapPin className="mr-2 h-4 w-4" />
                {isEditing ? "Update Stop" : "Add Stop"}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>


      {/* Delete Section - Only show when editing */}
      {isEditing && (
        <div className="p-4 border-t border-gray-200 bg-white">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="destructive" 
                className="w-full"
                disabled={deleteStopMutation.isPending}
              >
                <Trash2 className="h-4 w-4" />
                {deleteStopMutation.isPending ? "Deleting..." : "Delete Stop"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Stop</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this stop? This action cannot be undone.
                  {stopData?.title && (
                    <span className="block mt-2 font-medium text-gray-900">
                      "{stopData.title}"
                    </span>
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => deleteStopMutation.mutate()}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      <SavedStopsSheet
        isOpen={isSavedStopsSheetOpen}
        onClose={() => setIsSavedStopsSheetOpen(false)}
        itineraryId={itineraryId}
        dayId={selectedDayId}
        nextStopIndex={nextStopIndex}
      />

      {/* Markup Editor Sheet */}
      <MarkupEditorSheet
        isOpen={isMarkupEditorOpen}
        onOpenChange={setIsMarkupEditorOpen}
        value={watch('description') || ''}
        onSave={(value: string) => {
          setValue('description', value, { shouldDirty: true })
          setIsMarkupEditorOpen(false)
        }}
      />

      {/* Highlight Markup Editor Sheet */}
      <MarkupEditorSheet
        isOpen={isHighlightMarkupEditorOpen}
        onOpenChange={setIsHighlightMarkupEditorOpen}
        value={watch('highlight') || ''}
        onSave={(value: string) => {
          setValue('highlight', value, { shouldDirty: true })
          setIsHighlightMarkupEditorOpen(false)
        }}
      />

      <ChatButton />
    </div>
  )
} 